import { useCallback, useEffect, useRef, useState } from 'react'

// Thin MediaRecorder wrapper for chat voice notes. start() asks for
// the mic and begins capture; stop() resolves the recorded Blob;
// cancel() throws the recording away. `seconds` ticks up while
// recording so the UI can show a timer. `supported` is false where
// MediaRecorder / getUserMedia don't exist (very old browsers).
export function useVoiceRecorder() {
  const [recording, setRecording] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [error, setError] = useState(null)

  const recorderRef = useRef(null)
  const chunksRef = useRef([])
  const streamRef = useRef(null)
  const timerRef = useRef(null)
  const resolveRef = useRef(null)

  const supported = typeof window !== 'undefined'
    && !!navigator?.mediaDevices?.getUserMedia
    && typeof window.MediaRecorder !== 'undefined'

  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tk) => tk.stop())
      streamRef.current = null
    }
    recorderRef.current = null
    chunksRef.current = []
  }, [])

  useEffect(() => () => cleanup(), [cleanup])

  const start = useCallback(async () => {
    setError(null)
    if (!supported) { setError('unsupported'); return false }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const rec = new MediaRecorder(stream)
      chunksRef.current = []
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data) }
      rec.onstop = () => {
        const type = rec.mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type })
        const resolve = resolveRef.current
        resolveRef.current = null
        cleanup()
        setRecording(false)
        setSeconds(0)
        resolve?.(blob)
      }
      recorderRef.current = rec
      rec.start()
      setRecording(true)
      setSeconds(0)
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000)
      return true
    } catch (e) {
      setError(e?.name === 'NotAllowedError' ? 'denied' : 'failed')
      cleanup()
      setRecording(false)
      return false
    }
  }, [supported, cleanup])

  // Resolves with the recorded Blob (or null if nothing captured).
  const stop = useCallback(() => {
    return new Promise((resolve) => {
      const rec = recorderRef.current
      if (!rec || rec.state === 'inactive') { resolve(null); return }
      resolveRef.current = resolve
      rec.stop()
    })
  }, [])

  const cancel = useCallback(() => {
    const rec = recorderRef.current
    resolveRef.current = null
    if (rec && rec.state !== 'inactive') {
      rec.onstop = null
      try { rec.stop() } catch { /* already stopped */ }
    }
    cleanup()
    setRecording(false)
    setSeconds(0)
  }, [cleanup])

  return { supported, recording, seconds, error, start, stop, cancel }
}
